type RGB = {
	r: number;
	g: number;
	b: number;
};

type HSL = {
	h: number;
	s: number;
	l: number;
};

type ImageColor = {
	count: number;
	hex: string;
	rgb: RGB;
	hsl: HSL;
};

const decendingCount = (a: ImageColor, b: ImageColor) => b.count - a.count;
const ascendingCount = (a: ImageColor, b: ImageColor) => a.count - b.count;

export function hexToRgb(hex: string): RGB {
	const result = (/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/iu).exec(hex);

	return result ? {
		b: parseInt(result[ 3 ], 16),
		g: parseInt(result[ 2 ], 16),
		r: parseInt(result[ 1 ], 16)
	} : {
		b: 0,
		g: 0,
		r: 0
	};
}

export function rgbToHex(rgb: RGB): string {
	// eslint-disable-next-line no-bitwise
	return `#${((1 << 24) + (rgb.r << 16) + (rgb.g << 8) + rgb.b).toString(16).slice(1)}`;
}

export function rgbToHsl(rgb: RGB): { h: number, s: number, l: number } {
	const r = rgb.r / 255;
	const g = rgb.g / 255;
	const b = rgb.b / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;

	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
		case r: h = ((g - b) / d) + (g < b ? 6 : 0); break;
		case g: h = ((b - r) / d) + 2; break;
		case b: h = ((r - g) / d) + 4; break;
		default: h = ((g - b) / d) + 0; break;
		}
		h /= 6;
	}

	return {
		h,
		l,
		s
	};
}

export async function getImageColorCounts(imageUrl: string, blockSize = 1): Promise<Record<string, number>> {
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');

	if (!context) {
		return {};
	}

	const response = await fetch(imageUrl);
	const blob = await response.blob();
	const img = new Image();
	img.src = URL.createObjectURL(blob);

	const imgData = await new Promise<ImageData | null>((resolve) => {
		img.onload = () => {
			canvas.width = img.width;
			canvas.height = img.height;
			context.drawImage(img, 0, 0);
			try {
				resolve(context.getImageData(0, 0, canvas.width, canvas.height));
			} catch (e) {
				resolve(null);
			}
		};
	});

	if (!imgData) {
		return {};
	}

	const colorCounts: Record<string, number> = {};
	for (let i = 0; i < imgData.data.length; i += blockSize * 4) {
		const rgb = {
			b: imgData.data[ i + 2 ],
			g: imgData.data[ i + 1 ],
			r: imgData.data[ i ]
		};

		const key = rgbToHex(rgb);
		colorCounts[ key ] = (colorCounts[ key ] || 0) + 1;
	}

	return colorCounts;
}

export async function getLogoColors(logoUrl: string): Promise<ImageColor[]> {
	const lightnessThreshold = 0.85;
	const darknessThreshold = 0.3;
	const saturationThreshold = 0.3;

	const colorCounts = await getImageColorCounts(logoUrl);

	const colors = Object.entries(colorCounts).map(([ hex, count ]) => {
		const rgb = hexToRgb(hex);
		const hsl = rgbToHsl(rgb);

		return {
			count,
			hex,
			hsl,
			rgb
		};
	});

	const isWithinSaturationThreshold = (lightness: number, saturation: number) =>
		lightness > darknessThreshold && lightness < lightnessThreshold && saturation > saturationThreshold;

	const saturatedColors = colors.filter(({ hsl }) => isWithinSaturationThreshold(hsl.l, hsl.s));

	const desaturatedColors = colors.filter(({ hsl }) => !isWithinSaturationThreshold(hsl.l, hsl.s));

	return [
		...saturatedColors.sort(decendingCount),
		...desaturatedColors.sort(ascendingCount)
	];
}
